import React, { useEffect, useRef, useState } from "react";
import UserService from "../../services/userService";
import LoadingOverlay from "components/ui/LoadingOverlay";
import LoadingMark from "components/ui/LoadingMark";
import PageHeader from "components/PageHeader";
import { PAGE_CONTENT_CLASS } from "../../components/layout/PageContent";

const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg";
const MAX_FILE_SIZE_MB = 4;

function BrandingImageField({
  id,
  label,
  hint,
  previewUrl,
  file,
  onFileChange,
  onRemove,
  canRemove,
}) {
  const inputRef = useRef(null);
  const objectUrl = file ? URL.createObjectURL(file) : null;
  const displayUrl = objectUrl || previewUrl || null;

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  return (
    <div className="space-y-3">
      <div>
        <label
          htmlFor={id}
          className="block text-sm font-medium text-foreground"
        >
          {label}
        </label>
        {hint ? (
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        ) : null}
      </div>

      {displayUrl ? (
        <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-3">
          <img
            src={displayUrl}
            alt={`Pré-visualização ${label}`}
            className="mx-auto max-h-20 w-auto max-w-full object-contain"
          />
          {canRemove ? (
            <button
              type="button"
              onClick={onRemove}
              className="
                w-full px-4 py-2 rounded-md text-sm font-medium
                border border-destructive/30 text-destructive
                hover:bg-destructive/5 transition-colors
              "
            >
              Remover logo
            </button>
          ) : null}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-muted/10 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum logo enviado
        </div>
      )}

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        className="hidden"
        onChange={(e) => {
          const selected = e.target.files?.[0] ?? null;
          onFileChange(selected);
        }}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="
          px-4 py-2 rounded-md text-sm font-medium
          border border-border bg-background
          hover:bg-muted/50 transition-colors
        "
      >
        {displayUrl ? "Trocar logo" : "Selecionar logo"}
      </button>
    </div>
  );
}

const UserConfiguration = () => {
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState("");
  const [footerPreviewUrl, setFooterPreviewUrl] = useState("");
  const [headerFile, setHeaderFile] = useState(null);
  const [footerFile, setFooterFile] = useState(null);
  const [removeHeader, setRemoveHeader] = useState(false);
  const [removeFooter, setRemoveFooter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const response = await UserService.loadUserConfigurations();
        const rawConfig = response?.configuration ?? {};

        setHeaderPreviewUrl(rawConfig.header_image_url || "");
        setFooterPreviewUrl(rawConfig.footer_image_url || "");
      } catch {
        setError("Erro ao carregar configurações do usuário.");
      } finally {
        setLoading(false);
      }
    };

    loadConfiguration();
  }, []);

  const validateFile = (file) => {
    if (!file) return null;

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      return "Use apenas imagens PNG ou JPG.";
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `O logo deve ter no máximo ${MAX_FILE_SIZE_MB} MB.`;
    }

    return null;
  };

  const hasPendingChanges =
    Boolean(headerFile) ||
    Boolean(footerFile) ||
    removeHeader ||
    removeFooter;

  const handleRemoveHeader = () => {
    if (headerFile) {
      setHeaderFile(null);
      return;
    }
    if (headerPreviewUrl) {
      setHeaderPreviewUrl("");
      setRemoveHeader(true);
    }
    setSuccessMessage("");
  };

  const handleRemoveFooter = () => {
    if (footerFile) {
      setFooterFile(null);
      return;
    }
    if (footerPreviewUrl) {
      setFooterPreviewUrl("");
      setRemoveFooter(true);
    }
    setSuccessMessage("");
  };

  const handleSave = async () => {
    if (!hasPendingChanges) {
      setError("Selecione um logo, remova um logo existente, ou faça ambos.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      const headerError = validateFile(headerFile);
      const footerError = validateFile(footerFile);
      if (headerError || footerError) {
        throw new Error(headerError || footerError);
      }

      const formData = new FormData();
      if (headerFile) formData.append("header", headerFile);
      if (footerFile) formData.append("footer", footerFile);
      if (removeHeader) formData.append("remove_header", "true");
      if (removeFooter) formData.append("remove_footer", "true");

      const result = await UserService.saveUserConfigurations(formData);

      if (!result?.success) {
        throw new Error(
          result?.message || "Não foi possível salvar os logos."
        );
      }

      const refreshed = await UserService.loadUserConfigurations();
      const rawConfig = refreshed?.configuration ?? {};
      setHeaderPreviewUrl(rawConfig.header_image_url || "");
      setFooterPreviewUrl(rawConfig.footer_image_url || "");
      setHeaderFile(null);
      setFooterFile(null);
      setRemoveHeader(false);
      setRemoveFooter(false);
      setSuccessMessage("Logos salvos com sucesso.");
    } catch (err) {
      setError(
        err?.message || "Erro ao salvar os logos de cabeçalho e rodapé."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`${PAGE_CONTENT_CLASS} flex justify-center py-16`}>
        <LoadingMark message="Carregando configurações..." />
      </div>
    );
  }

  return (
    <>
      {saving && <LoadingOverlay message="Salvando logos..." />}
      <div className={PAGE_CONTENT_CLASS}>
        <div className="space-y-8">
          <PageHeader
            title="Logo cabeçalho e logo rodapé"
            subtitle="Envie logos compactos que aparecerão no topo e na base de cada página dos documentos manuais e dos documentos gerados com modelos personalizados."
            icon="Settings"
          />

          <div className="bg-white border border-border rounded-xl shadow-sm p-6 md:p-8 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <BrandingImageField
                id="header-image"
                label="Logo cabeçalho"
                hint="PNG ou JPG. Prefira logos horizontais e compactos (recomendado: faixa ~800×120 px, até 4 MB)."
                previewUrl={headerPreviewUrl}
                file={headerFile}
                onFileChange={(file) => {
                  setHeaderFile(file);
                  if (file) setRemoveHeader(false);
                }}
                onRemove={handleRemoveHeader}
                canRemove={Boolean(headerPreviewUrl || headerFile)}
              />

              <BrandingImageField
                id="footer-image"
                label="Logo rodapé"
                hint="PNG ou JPG. Prefira logos horizontais e compactos (recomendado: faixa ~800×100 px, até 4 MB)."
                previewUrl={footerPreviewUrl}
                file={footerFile}
                onFileChange={(file) => {
                  setFooterFile(file);
                  if (file) setRemoveFooter(false);
                }}
                onRemove={handleRemoveFooter}
                canRemove={Boolean(footerPreviewUrl || footerFile)}
              />
            </div>

            {error ? <div className="text-sm text-destructive">{error}</div> : null}

            {successMessage ? (
              <div className="text-sm text-green-600">{successMessage}</div>
            ) : null}

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !hasPendingChanges}
                className="
                  px-6 py-2.5 rounded-md font-medium
                  bg-primary text-primary-foreground
                  hover:bg-primary/90
                  focus:outline-none focus:ring-2 focus:ring-primary/40
                  transition-colors
                  disabled:opacity-60
                "
              >
                {saving ? "Salvando..." : "Salvar logos"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserConfiguration;
